/**
 * HelpMiniMock.tsx — the "snapshot" canvas of the page-help dialog.
 *
 * Draws a stylized mini-mock of the real page (two-column skeleton built from
 * plain MUI Boxes — no screenshots) with numbered marker dots ①②③ pinned to
 * its regions. Clicking a marker (or the region itself) selects it: the
 * region highlights and the dialog shows that item's explanation beside it.
 *
 * Geometry comes from the HelpContent definition (percent-based rects), so
 * this renderer is content-agnostic. Theme tokens only — no hex literals.
 */

import { Box, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { HelpMockArea, HelpMockSketch } from './helpTypes';

// ── Sketch primitives (skeleton placeholders) ─────────────────────────────────

const line = (theme: Theme, widthPct: number, height = 6) => (
  <Box sx={{
    width: `${widthPct}%`,
    height,
    borderRadius: 1,
    backgroundColor: alpha(theme.palette.text.secondary, 0.22),
    flexShrink: 0,
  }} />
);

const pill = (theme: Theme, width: number, filled = false, color?: string) => (
  <Box sx={{
    width,
    height: 10,
    borderRadius: 5,
    flexShrink: 0,
    backgroundColor: filled
      ? alpha(color ?? theme.palette.primary.main, 0.7)
      : 'transparent',
    border: `1px solid ${alpha(color ?? theme.palette.text.secondary, 0.45)}`,
  }} />
);

const fieldStub = (theme: Theme) => (
  <Box sx={{
    flex: 1,
    minWidth: 0,
    height: 12,
    borderRadius: 1,
    border: `1px solid ${theme.border.color}`,
    backgroundColor: theme.palette.background.paper,
  }} />
);

// ── Per-kind sketch content ───────────────────────────────────────────────────

const Sketch = ({ kind }: { kind: HelpMockSketch }) => {
  const theme = useTheme();
  const rowSx = { display: 'flex', alignItems: 'center', gap: 0.5 } as const;

  switch (kind) {
    case 'titleBar':
      return (
        <Box sx={{ ...rowSx, height: '100%' }}>
          <Box sx={{
            width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
            backgroundColor: alpha(theme.palette.primary.main, 0.8),
          }} />
          {line(theme, 34, 8)}
          {pill(theme, 44, true)}
          {pill(theme, 36, false, theme.admin.mappingUnconfirmed)}
        </Box>
      );
    case 'toolbar':
      return (
        <Box sx={{ ...rowSx, height: '100%', justifyContent: 'flex-end' }}>
          {pill(theme, 30)}{pill(theme, 26)}{pill(theme, 30)}{pill(theme, 26)}
          {pill(theme, 34, true)}
        </Box>
      );
    case 'listColumn':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%' }}>
          <Box sx={{
            height: 14, borderRadius: 1, flexShrink: 0,
            border: `1px solid ${theme.border.color}`,
            backgroundColor: theme.palette.background.paper,
          }} />
          <Box sx={rowSx}>{pill(theme, 22)}{pill(theme, 30)}{pill(theme, 26)}</Box>
          {[0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{
              flex: 1, minHeight: 0, borderRadius: 1, p: 0.5,
              border: `1px solid ${theme.border.color}`,
              backgroundColor: theme.palette.background.paper,
              display: 'flex', flexDirection: 'column', gap: 0.5,
              justifyContent: 'center',
            }}>
              {line(theme, 62)}
              <Box sx={rowSx}>
                {pill(theme, 22, false, theme.schedule.machineTime)}
                {pill(theme, 30, i === 0, theme.admin.mappingUnconfirmed)}
              </Box>
            </Box>
          ))}
        </Box>
      );
    case 'formPane':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, height: '100%' }}>
          {[0, 1].map((section) => (
            <Box key={section} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
              {line(theme, 26, 8)}
              <Box sx={rowSx}>{fieldStub(theme)}{fieldStub(theme)}</Box>
              <Box sx={rowSx}>{fieldStub(theme)}{fieldStub(theme)}</Box>
            </Box>
          ))}
        </Box>
      );
    case 'pickerPane':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%' }}>
          <Box sx={{
            height: 16, borderRadius: 1, flexShrink: 0,
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.55)}`,
            backgroundColor: theme.palette.background.paper,
          }} />
          {[0, 1].map((i) => (
            <Box key={i} sx={{
              ...rowSx, flex: 1, minHeight: 0, borderRadius: 1, px: 0.5,
              border: `1px dashed ${alpha(theme.admin.mappingSuggestion, 0.6)}`,
              backgroundColor: alpha(theme.admin.mappingSuggestion, 0.05),
            }}>
              {line(theme, 44)}
              <Box sx={{ flex: 1 }} />
              {pill(theme, 30, true)}
            </Box>
          ))}
        </Box>
      );
    case 'chipRow':
      return (
        <Box sx={{ ...rowSx, height: '100%' }}>
          <Box sx={{
            width: 22, height: 12, borderRadius: 6, flexShrink: 0,
            backgroundColor: alpha(theme.palette.primary.main, 0.55),
          }} />
          {line(theme, 30)}
          <Box sx={{ flex: 1 }} />
          {pill(theme, 34, false, theme.schedule.conflictHard)}
        </Box>
      );
    case 'chainRow':
      return (
        <Box sx={{ ...rowSx, height: '100%' }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ ...rowSx, flex: 1, minWidth: 0, gap: 0 }}>
              <Box sx={{
                flex: 1, height: 22, borderRadius: 1,
                border: `1px solid ${alpha(theme.schedule.machineTime, 0.55)}`,
                backgroundColor: alpha(theme.schedule.machineTime, 0.08),
              }} />
              {i < 2 && (
                <Box sx={{
                  width: 12, height: 2, flexShrink: 0,
                  backgroundColor: alpha(theme.palette.text.secondary, 0.4),
                }} />
              )}
            </Box>
          ))}
        </Box>
      );
    case 'footerBar':
      return (
        <Box sx={{ ...rowSx, height: '100%', justifyContent: 'flex-end' }}>
          {pill(theme, 28, false, theme.palette.error.main)}
          {pill(theme, 28)}
          {pill(theme, 34, true)}
        </Box>
      );
  }
};

// ── The mock canvas ───────────────────────────────────────────────────────────

interface HelpMiniMockProps {
  areas: HelpMockArea[];
  /** id → marker number (order of snapshot items). */
  numberById: Record<string, number>;
  selectedId: string;
  onSelect: (id: string) => void;
}

export const HelpMiniMock = ({ areas, numberById, selectedId, onSelect }: HelpMiniMockProps) => {
  const theme = useTheme();
  return (
    <Box
      role="img"
      aria-label="Simplified sketch of this page's layout with numbered regions"
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 11',
        borderRadius: 2,
        border: `1px solid ${theme.border.color}`,
        backgroundColor: theme.palette.background.default,
        overflow: 'hidden',
      }}
    >
      {areas.map((area) => {
        const selected = area.id === selectedId;
        return (
          <Box
            key={area.id}
            onClick={() => onSelect(area.id)}
            sx={{
              position: 'absolute',
              left: `${area.rect.left}%`,
              top: `${area.rect.top}%`,
              width: `${area.rect.width}%`,
              height: `${area.rect.height}%`,
              borderRadius: 1.5,
              p: 0.75,
              cursor: 'pointer',
              transition: 'border-color 120ms, background-color 120ms',
              border: `2px solid ${selected ? theme.palette.primary.main : alpha(theme.border.color, 0.9)}`,
              backgroundColor: selected
                ? alpha(theme.palette.primary.main, 0.07)
                : theme.palette.background.paper,
              '&:hover': { borderColor: theme.palette.primary.main },
            }}
          >
            <Sketch kind={area.sketch} />
          </Box>
        );
      })}
      {areas.map((area) => {
        const selected = area.id === selectedId;
        const num = numberById[area.id];
        return (
          <Box
            key={`marker-${area.id}`}
            component="button"
            type="button"
            aria-label={`Highlight region ${num}`}
            aria-pressed={selected}
            onClick={() => onSelect(area.id)}
            sx={{
              position: 'absolute',
              left: `${area.marker.left}%`,
              top: `${area.marker.top}%`,
              transform: 'translate(-50%, -50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
              border: `2px solid ${theme.palette.primary.main}`,
              color: selected ? theme.palette.primary.contrastText : theme.palette.primary.main,
              backgroundColor: selected ? theme.palette.primary.main : theme.palette.background.paper,
              boxShadow: theme.shadows[selected ? 3 : 1],
              transition: 'background-color 120ms, color 120ms',
            }}
          >
            {num}
          </Box>
        );
      })}
    </Box>
  );
};
